<template>
  <div class="layout">
    <nav-bar
      :logoSrc="$maxxpm.config.logo.src"
      :logoAlt="$maxxpm.config.logo.alt"
      :bannerTitle="undefined"
      :bannerSubtitle="undefined"
      :bannerCTA="undefined"
    />
    <div
      v-if="promoItems.length"
      id="incentive"
      class="promo-carousel"
    >
      <div class="promo-carousel-content">
        <p class="promo-message">
          {{ currentPromoMessage }}
        </p>
        <Button
          label="View Availability"
          class="promo-cta"
          @click="goToAvailability"
        />
      </div>
      <div
        v-if="promoItems.length > 1"
        class="promo-dots"
      >
        <button
          v-for="(item, index) in promoItems"
          :key="`${item.desktopMessage}-${item.mobileMessage}-${index}`"
          type="button"
          class="promo-dot"
          :class="{ active: currentPromoIndex === index }"
          :aria-label="`View promotion ${index + 1}`"
          @click="setPromoIndex(index)"
        ></button>
      </div>
      <p class="promo-disclaimer">*Offer subject to availability. Contact leasing for more details.</p>
    </div>
    <main>
      <router-view />
      <Dialog
        v-model:visible="showPopup"
        :modal="true"
        :dismissableMask="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
      >
        <template #header>
          <h2 class="text-center flex-grow-1">Don't wait, schedule to visit the property today!</h2>
        </template>
        <div class="flex justify-content-center p-4 popup-showing">
          <Button
            @click="openShowingPopup"
            aria-haspopup="dialog"
            label="Schedule a Showing"
          ></Button>
        </div>
      </Dialog>
      <Dialog
        v-model:visible="showPromoPopup"
        :modal="true"
        :dismissableMask="true"
        :style="{ width: '32rem' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
        class="promo-popup-dialog"
      >
        <template #header>
          <h2 class="text-center flex-grow-1 mb-0">Limited Time Offers</h2>
        </template>
        <div
          v-if="currentPromoSlide"
          class="promo-popup"
        >
          <div class="promo-popup-media">
            <img
              :src="currentPromoSlide.imageUrl"
              :alt="currentPromoSlide.imageAlt"
              class="promo-popup-image"
            />
            <button
              v-if="promoPopupSlides.length > 1"
              type="button"
              class="promo-popup-nav promo-popup-nav-prev"
              aria-label="Previous promotion"
              @click="prevPromoSlide"
            >
              <i class="pi pi-chevron-left" aria-hidden="true"></i>
            </button>
            <button
              v-if="promoPopupSlides.length > 1"
              type="button"
              class="promo-popup-nav promo-popup-nav-next"
              aria-label="Next promotion"
              @click="nextPromoSlide"
            >
              <i class="pi pi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
          <div class="promo-popup-body">
            <p
              v-if="currentPromoSlide.unitLabel"
              class="promo-popup-unit"
            >
              {{ currentPromoSlide.unitLabel }}
            </p>
            <p class="promo-popup-message">{{ currentPromoSlide.message }}</p>
            <p class="promo-popup-disclaimer">*Offer subject to availability. Contact leasing for more details.</p>
            <div class="promo-popup-actions">
              <Button
                v-if="currentPromoSlide.hasShowing"
                label="Schedule a Showing"
                class="promo-popup-cta"
                @click="openPromoShowing"
              />
              <Button
                label="View This Home"
                :class="['promo-popup-cta', { 'p-button-outlined': currentPromoSlide.hasShowing }]"
                @click="goToPromoListing"
              />
            </div>
            <div
              v-if="promoPopupSlides.length > 1"
              class="promo-popup-dots"
            >
              <button
                v-for="(slide, index) in promoPopupSlides"
                :key="`${slide.listingId}-${slide.promoId}-${index}`"
                type="button"
                class="promo-popup-dot"
                :class="{ active: currentPromoSlideIndex === index }"
                :aria-label="`View promotion ${index + 1}`"
                @click="setPromoSlideIndex(index)"
              ></button>
            </div>
          </div>
        </div>
      </Dialog>
    </main>
    <footer>
      <app-footer
        :communityName="$maxxpm.config.communityName"
        :streetAddress="$maxxpm.config.footer.address"
        :cityStateZip="$maxxpm.config.footer.cityStateZip"
        :phone="$maxxpm.config.footer.phone"
        :logoUrl="$maxxpm.config.footer.logo ?? $maxxpm.config.logo.src"
        :copyrightYear="$maxxpm.config.footer.copyrightYear"
      />
    </footer>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import axios from "axios";
import router from "./router";
import bookingMessageHandler from "./utils/booking_message_handler";

const $maxxpm = inject("$maxxpm");
const LISTING_SOURCE = "thevillageongreenwayapts.com";

const showPopup = ref(false);
const showPromoPopup = ref(false);
const Calendly = ref(null);
const QRG = ref(null);
const promoItems = ref([]);
const promoPopupSlides = ref([]);
const currentPromoIndex = ref(0);
const currentPromoSlideIndex = ref(0);
const isDesktopPromo = ref(false);
let promoIntervalId = null;
let promoPopupIntervalId = null;

function getSortedPhotos(photos) {
  return [...(photos ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

function getListingImage(listing) {
  const firstPhoto = getSortedPhotos(listing.photos)[0];
  return firstPhoto?.url ?? firstPhoto?.thumbnailUrl ?? $maxxpm.config.home.banner.image;
}

function getUnitLabel(listing) {
  if (listing.bedrooms != null && listing.bathrooms != null) {
    return `${listing.bedrooms} Bed · ${listing.bathrooms} Bath`;
  }

  if (listing.marketingTitle) {
    return listing.marketingTitle;
  }

  return listing.address1 ?? "";
}

function normalizePromotionItems(rawPromotions) {
  const unique = new Set();
  const normalized = [];

  rawPromotions.forEach((promotion) => {
    const desktopMessage = promotion.description;
    const mobileMessage = promotion.badge;
    const key = promotion.id;

    if (!unique.has(key)) {
      unique.add(key);
      normalized.push({
        desktopMessage,
        mobileMessage,
      });
    }
  });

  return normalized;
}

function getListingShowingInfo(listing) {
  const community = listing.community ?? null;
  const microsoftBookingServiceId = listing.microsoftBookingServiceId || community?.microsoftBookingServiceId || null;
  const showingUrl = listing.showingUrl || community?.showingUrl || null;

  return {
    hasShowing: Boolean(microsoftBookingServiceId || showingUrl),
    microsoftBookingServiceId: listing.microsoftBookingServiceId || null,
    communityMicrosoftBookingServiceId: community?.microsoftBookingServiceId || null,
    communityId: community?.id || null,
    showingUrl: listing.showingUrl || null,
    communityShowingUrl: community?.showingUrl || null,
  };
}

function buildPromoPopupSlides(listings) {
  const fallbackImage = $maxxpm.config.home.banner.image;

  return (listings ?? []).flatMap((listing) => {
    const promotions = listing.promotions ?? [];
    if (!promotions.length) {
      return [];
    }

    const imageUrl = getListingImage(listing) || fallbackImage;
    const unitLabel = getUnitLabel(listing);
    const showingInfo = getListingShowingInfo(listing);
    const seenPromoIds = new Set();

    return promotions.flatMap((promotion) => {
      if (seenPromoIds.has(promotion.id)) {
        return [];
      }

      seenPromoIds.add(promotion.id);

      return [{
        listingId: listing.id,
        promoId: promotion.id,
        message: promotion.description,
        imageUrl,
        imageAlt: listing.marketingTitle || unitLabel || $maxxpm.config.communityName,
        unitLabel,
        listingUrl: `https://maxxpropertymanagement.com/rentals/${listing.id}?utm_source=${LISTING_SOURCE}`,
        ...showingInfo,
      }];
    });
  });
}

async function fetchListings() {
  const baseUrl = process.env.VUE_APP_LISTINGS_API_URL;
  const propertyGroup = $maxxpm.config.availability.propertyGroup;

  if (!baseUrl) {
    return [];
  }

  try {
    const listingsResponse = await axios.get(`${baseUrl}/listings?property_group=${propertyGroup}`);
    return listingsResponse.data ?? [];
  } catch (error) {
    return [];
  }
}

function setPromoIndex(index) {
  currentPromoIndex.value = index;
}

function setPromoSlideIndex(index) {
  currentPromoSlideIndex.value = index;
}

function nextPromoSlide() {
  if (!promoPopupSlides.value.length) {
    return;
  }

  currentPromoSlideIndex.value = (currentPromoSlideIndex.value + 1) % promoPopupSlides.value.length;
}

function prevPromoSlide() {
  if (!promoPopupSlides.value.length) {
    return;
  }

  currentPromoSlideIndex.value =
    (currentPromoSlideIndex.value - 1 + promoPopupSlides.value.length) % promoPopupSlides.value.length;
}

function goToAvailability() {
  router.push({ path: "/availability" });
}

function goToPromoListing() {
  const slide = currentPromoSlide.value;
  if (!slide?.listingUrl) {
    return;
  }

  showPromoPopup.value = false;
  window.open(slide.listingUrl, "_blank", "noopener,noreferrer");
}

function openPromoShowing() {
  const slide = currentPromoSlide.value;
  if (!slide?.hasShowing) {
    return;
  }

  const utmSource = urlParams?.get("utm_source") || LISTING_SOURCE;

  if (slide.microsoftBookingServiceId || slide.communityMicrosoftBookingServiceId) {
    QRG.value?.open({ communityId: slide.communityId, listingId: slide.listingId });
    return;
  }

  const showingUrl = slide.showingUrl || slide.communityShowingUrl;
  if (!showingUrl || !Calendly.value) {
    return;
  }

  Calendly.value.initPopupWidget({
    url: showingUrl,
    utm: {
      utmSource,
      utmMedium: "listing",
      utmContent: slide.listingId,
    },
  });
}

function updatePromoViewportState() {
  isDesktopPromo.value = window.innerWidth > 768;
}

function getPromoMessageByIndex(index) {
  const promo = promoItems.value[index];
  if (!promo) {
    return "";
  }

  return isDesktopPromo.value ? promo.desktopMessage : promo.mobileMessage;
}

function startPromoRotation() {
  if (promoIntervalId || promoItems.value.length <= 1) {
    return;
  }

  promoIntervalId = window.setInterval(() => {
    currentPromoIndex.value = (currentPromoIndex.value + 1) % promoItems.value.length;
  }, 5000);
}

function startPromoPopupRotation() {
  if (promoPopupIntervalId || promoPopupSlides.value.length <= 1) {
    return;
  }

  promoPopupIntervalId = window.setInterval(() => {
    nextPromoSlide();
  }, 10000);
}

function stopPromoPopupRotation() {
  if (promoPopupIntervalId) {
    window.clearInterval(promoPopupIntervalId);
    promoPopupIntervalId = null;
  }
}

const currentPromoMessage = computed(() => {
  return getPromoMessageByIndex(currentPromoIndex.value);
});

const currentPromoSlide = computed(() => {
  return promoPopupSlides.value[currentPromoSlideIndex.value] ?? null;
});

watch(showPromoPopup, (visible) => {
  if (visible) {
    startPromoPopupRotation();
  } else {
    stopPromoPopupRotation();
  }
});

const getListingAvailableForShowing = async () => {
  const listings = await axios.get(`${process.env.VUE_APP_LISTINGS_API_URL}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`);
  if (listings.data.length) {
    const availableForShowing = listings.data.find((listing) => {
      if (listing.microsoftBookingServiceId || listing.community.microsoftBookingServiceId || listing.showingUrl || listing.community.showingUrl) {
        if (listing.readyForShowingOn) {
          return new Date(listing.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.readyForShowingOn) {
          return new Date(listing.activeUnit.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.availableOn) {
          const availableOn = new Date(listing.activeUnit.availableOn);
          const sevenDaysBeforeAvailableOn = new Date(availableOn.getTime());
          sevenDaysBeforeAvailableOn.setUTCDate(availableOn.getUTCDate() - 7);
          return sevenDaysBeforeAvailableOn <= new Date();
        }
      }
      return false;
    });
    return availableForShowing;
  }
  return null;
};

let urlParams;
const availableForShowing = ref(null);

async function openShowingPopup() {
  if (availableForShowing.value) {
    if (availableForShowing.value.microsoftBookingServiceId || availableForShowing.value.community.microsoftBookingServiceId) {
      QRG.value.open({ communityId: availableForShowing.value.community.id, listingId: availableForShowing.value.id });
    } else if (availableForShowing.value.showingUrl) {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "thevillageongreenwayapts.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    } else {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.community.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "thevillageongreenwayapts.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    }
  }
}

onMounted(async () => {
  Calendly.value = window.Calendly;
  updatePromoViewportState();
  window.addEventListener("resize", updatePromoViewportState);
  window.addEventListener("message", bookingMessageHandler);
  QRG.value = window.QrgBooking;
  availableForShowing.value = await getListingAvailableForShowing();
  urlParams = new URLSearchParams(window.location.search);

  const listings = await fetchListings();
  const fromListings = listings.flatMap((listing) => listing.promotions ?? []);
  promoItems.value = normalizePromotionItems(fromListings);
  promoPopupSlides.value = buildPromoPopupSlides(listings);
  startPromoRotation();

  const showingCookie = "showPopup";
  const promoCookie = "showPromoPopup";

  setTimeout(() => {
    if (availableForShowing.value && $maxxpm.config.hasShowingPopup && !document.cookie.split("; ").find((c) => c.startsWith(showingCookie))) {
      document.cookie = `${showingCookie}=true; max-age=${60 * 60 * 24 * 2}`;
      showPopup.value = true;
      return;
    }

    if (
      $maxxpm.config.hasPromotionPopup &&
      promoPopupSlides.value.length &&
      !document.cookie.split("; ").find((c) => c.startsWith(promoCookie))
    ) {
      document.cookie = `${promoCookie}=true; max-age=${60 * 60 * 24 * 2}`;
      showPromoPopup.value = true;
      startPromoPopupRotation();
    }
  }, 3500);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updatePromoViewportState);

  if (promoIntervalId) {
    window.clearInterval(promoIntervalId);
    promoIntervalId = null;
  }
  stopPromoPopupRotation();
  window.removeEventListener("message", bookingMessageHandler);
});
</script>

<style>
#app {
  font-family: Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.layout {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  position: relative;
}

body {
  margin: 0;
}

.img-fluid {
  max-width: 100%;
  height: auto;
}

.promo-carousel {
  background-color: var(--primary-color);
  color: #fff;
  padding: 0.45rem 1rem 0.3rem;
}

.promo-carousel-content {
  width: min(1200px, 95vw);
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.promo-message {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
}

.promo-cta.p-button {
  background: #fff;
  color: var(--primary-color);
  border-color: #fff;
  font-weight: 700;
  white-space: nowrap;
  padding: 0.45rem 0.8rem;
  font-size: 0.85rem;
}

.promo-cta.p-button:enabled:hover {
  background: #f5f7fb;
  border-color: #f5f7fb;
  color: var(--primary-color);
}

.promo-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.3rem;
}

.promo-disclaimer {
  width: min(1200px, 95vw);
  margin: 0.25rem auto 0;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.25;
  opacity: 0.95;
}

.promo-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  border: 0;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 0;
}

.promo-dot.active {
  background: #fff;
}

.promo-popup-dialog .p-dialog-header {
  padding-bottom: 0.5rem;
}

.promo-popup-dialog .p-dialog-content {
  padding-top: 0.5rem;
}

.promo-popup {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.promo-popup-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #e8ecf1;
}

.promo-popup-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.promo-popup-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.promo-popup-nav-prev {
  left: 0.65rem;
}

.promo-popup-nav-next {
  right: 0.65rem;
}

.promo-popup-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.65rem;
  padding: 0 0.25rem 0.5rem;
}

.promo-popup-unit {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--primary-color);
}

.promo-popup-message {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: pre-line;
}

.promo-popup-disclaimer {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.3;
  opacity: 0.8;
}

.promo-popup-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
  width: 100%;
}

.promo-popup-cta.p-button {
  font-weight: 700;
  padding: 0.65rem 1.25rem;
}

.promo-popup-cta.p-button-outlined {
  background: transparent;
  color: var(--primary-color);
}

.promo-popup-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.15rem;
}

.promo-popup-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  border: 0;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  padding: 0;
}

.promo-popup-dot.active {
  background: var(--primary-color);
}

@media (max-width: 768px) {
  .promo-carousel-content {
    flex-direction: column;
    gap: 0.5rem;
  }

  .promo-cta.p-button {
    width: auto;
  }

  .promo-disclaimer {
    font-size: 0.82rem;
  }

  .promo-popup-message {
    font-size: 0.98rem;
  }
}
</style>
